import React, { useState } from 'react';
import { Button } from '../Button';
import { CheckIcon, PlusIcon, XIcon, AlertCircleIcon } from 'lucide-react';
interface FunctionSelectorProps {
  onFunctionsChange: (functions: SelectedFunction[]) => void;
}
export interface SelectedFunction {
  name: string;
  parameters: string[];
}
// Define valid parameter combinations for each function
const functionParameterOptions = {
  addAccount: [{
    id: 'debtor',
    params: ['debtorAcctId', 'tenantId'],
    label: 'Debtor Account'
  }, {
    id: 'creditor',
    params: ['creditorAcctId', 'tenantId'],
    label: 'Creditor Account'
  }],
  addEntity: [{
    id: 'creditor',
    params: ['creditorId', 'tenantId', 'CreDtTm'],
    label: 'Creditor Entity'
  }, {
    id: 'debtor',
    params: ['debtorId', 'tenantId', 'CreDtTm'],
    label: 'Debtor Entity'
  }],
  addAccountHolder: [{
    id: 'creditor',
    params: ['creditorId', 'creditorAcctId', 'CreDtTm', 'tenantId'],
    label: 'Creditor Account Holder'
  }, {
    id: 'debtor',
    params: ['debtorId', 'debtorAcctId', 'CreDtTm', 'tenantId'],
    label: 'Debtor Account Holder'
  }]
};
const availableFunctions = Object.keys(functionParameterOptions);
export const FunctionSelector: React.FC<FunctionSelectorProps> = ({
  onFunctionsChange
}) => {
  const [selectedFunctions, setSelectedFunctions] = useState<SelectedFunction[]>([]);
  const [showAddFunctionModal, setShowAddFunctionModal] = useState(false);
  const handleAddFunction = () => {
    setShowAddFunctionModal(true);
  };
  const handleRemoveFunction = (index: number) => {
    const newFunctions = selectedFunctions.filter((_, i) => i !== index);
    setSelectedFunctions(newFunctions);
    onFunctionsChange(newFunctions);
  };
  const handleFunctionChange = (index: number, field: keyof SelectedFunction, value: any) => {
    const newFunctions = [...selectedFunctions];
    if (field === 'name') {
      // When function name changes, reset parameters to first option's parameters
      const functionName = value as string;
      const firstOptionParams = functionParameterOptions[functionName][0].params;
      newFunctions[index] = {
        name: functionName,
        parameters: firstOptionParams
      };
    } else if (field === 'parameters') {
      newFunctions[index][field] = value as string[];
    }
    setSelectedFunctions(newFunctions);
    onFunctionsChange(newFunctions);
  };
  const AddFunctionModal = () => {
    const [functionName, setFunctionName] = useState(availableFunctions[0]);
    const [selectedOptionId, setSelectedOptionId] = useState(functionParameterOptions[availableFunctions[0]][0].id);
    const handleSave = () => {
      if (functionName) {
        const selectedOption = functionParameterOptions[functionName].find(option => option.id === selectedOptionId);
        if (selectedOption) {
          const newFunction = {
            name: functionName,
            parameters: selectedOption.params
          };
          setSelectedFunctions([...selectedFunctions, newFunction]);
          onFunctionsChange([...selectedFunctions, newFunction]);
          setShowAddFunctionModal(false);
        }
      }
    };
    return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" data-id="element-183">
        <div className="bg-white rounded-lg w-full max-w-md p-6" data-id="element-184">
          <div className="flex justify-between items-center mb-6" data-id="element-185">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-186">Add Function</h3>
            <button onClick={() => setShowAddFunctionModal(false)} className="text-gray-500 hover:text-gray-700" data-id="element-187">
              <XIcon size={20} data-id="element-188" />
            </button>
          </div>
          <div className="space-y-4" data-id="element-189">
            <div data-id="element-190">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-191">
                Select Function
              </label>
              <select value={functionName} onChange={e => {
              const newFunctionName = e.target.value;
              setFunctionName(newFunctionName);
              // Reset to first option when function changes
              setSelectedOptionId(functionParameterOptions[newFunctionName][0].id);
            }} className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-192">
                {availableFunctions.map(fn => <option key={fn} value={fn} data-id="element-193">
                    {fn}
                  </option>)}
              </select>
            </div>
            <div data-id="element-194">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-195">
                Select Parameter Configuration
              </label>
              <div className="space-y-3" data-id="element-196">
                {functionParameterOptions[functionName].map(option => <div key={option.id} className={`flex items-center p-3 rounded-md border cursor-pointer ${selectedOptionId === option.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`} onClick={() => setSelectedOptionId(option.id)} data-id="element-197">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 ${selectedOptionId === option.id ? 'bg-blue-500' : 'bg-gray-200'}`} data-id="element-198">
                      {selectedOptionId === option.id && <CheckIcon size={12} className="text-white" data-id="element-199" />}
                    </div>
                    <div className="flex-1" data-id="element-200">
                      <div className="text-sm font-medium" data-id="element-201">{option.label}</div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-202">
                        Parameters: {option.params.join(', ')}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6" data-id="element-203">
            <Button variant="secondary" onClick={() => setShowAddFunctionModal(false)} data-id="element-204">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={!functionName} data-id="element-205">
              Add Function
            </Button>
          </div>
        </div>
      </div>;
  };
  return <div className="space-y-6" data-id="element-206">
      <div className="flex justify-between items-center" data-id="element-207">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-208">Select Functions</h3>
        <Button variant="secondary" size="sm" onClick={handleAddFunction} icon={<PlusIcon size={16} data-id="element-210" />} data-id="element-209">
          Add Function
        </Button>
      </div>
      {selectedFunctions.length > 0 ? <div className="space-y-4" data-id="element-211">
          {selectedFunctions.map((func, index) => <div key={index} className="p-4 bg-gray-50 border border-gray-200 rounded-lg relative" data-id="element-212">
              <button onClick={() => handleRemoveFunction(index)} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600" data-id="element-213">
                <XIcon size={16} data-id="element-214" />
              </button>
              <div className="space-y-4" data-id="element-215">
                <div data-id="element-216">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-217">
                    Function
                  </label>
                  <select value={func.name} onChange={e => {
              handleFunctionChange(index, 'name', e.target.value);
            }} className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-218">
                    {availableFunctions.map(fn => <option key={fn} value={fn} data-id="element-219">
                        {fn}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-220">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-221">
                    Parameter Configuration
                  </label>
                  <div className="space-y-3" data-id="element-222">
                    {functionParameterOptions[func.name].map(option => {
                // Check if current function parameters match this option
                const isSelected = option.params.length === func.parameters.length && option.params.every(param => func.parameters.includes(param));
                return <div key={option.id} className={`flex items-center p-3 rounded-md border cursor-pointer ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`} onClick={() => handleFunctionChange(index, 'parameters', option.params)} data-id="element-223">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 ${isSelected ? 'bg-blue-500' : 'bg-gray-200'}`} data-id="element-224">
                            {isSelected && <CheckIcon size={12} className="text-white" data-id="element-225" />}
                          </div>
                          <div className="flex-1" data-id="element-226">
                            <div className="text-sm font-medium" data-id="element-227">
                              {option.label}
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-228">
                              Parameters: {option.params.join(', ')}
                            </div>
                          </div>
                        </div>;
              })}
                  </div>
                </div>
              </div>
            </div>)}
        </div> : <div className="text-center py-10 border border-gray-200 rounded-md" data-id="element-229">
          <p className="text-sm text-gray-500" data-id="element-230">
            No functions selected. Click "Add Function" to select functions to
            call at runtime.
          </p>
        </div>}
      {showAddFunctionModal && <AddFunctionModal data-id="element-231" />}
      {selectedFunctions.length > 0 && <div className="p-4 bg-blue-50 border border-blue-200 rounded-md" data-id="element-232">
          <div className="flex items-start" data-id="element-233">
            <AlertCircleIcon size={18} className="text-blue-500 mt-0.5 mr-2" data-id="element-234" />
            <div data-id="element-235">
              <h4 className="text-sm font-medium text-blue-700 mb-2" data-id="element-236">
                Function Configuration Summary
              </h4>
              <div className="space-y-2" data-id="element-237">
                {selectedFunctions.map((func, index) => <div key={index} className="text-sm text-blue-600" data-id="element-238">
                    <span className="font-medium" data-id="element-239">{func.name}</span>
                    <span data-id="element-240"> with parameters: </span>
                    <span className="font-medium" data-id="element-241">
                      {func.parameters.join(', ')}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>}
    </div>;
};